/** Versioned data models: the pure parts (what a diff says in words, which environment a deploy defaults to, what is selected). */
import type { DeployItem, ModelDiff, VirtualModel } from './api.ts';

/** "2 fields added · 1 removed · 1 changed · renamed", or why there is nothing to list. */
export function diffSummary(d: ModelDiff): string {
  if (d.initial) return 'First version';
  if (d.same) return 'No changes';
  const parts: string[] = [];
  if (d.renamed) parts.push('renamed');
  if (d.addedFields.length) parts.push(`${d.addedFields.length} added`);
  if (d.removedFields.length) parts.push(`${d.removedFields.length} removed`);
  if (d.changedFields.length) parts.push(`${d.changedFields.length} changed`);
  return parts.join(' · ');
}

/** A path key of a diff ("CRM.Customer·Name") as its class and path. */
export function splitPath(key: string): { cls: string; path: string } {
  const i = key.indexOf('·');
  return i < 0 ? { cls: '', path: key } : { cls: key.slice(0, i), path: key.slice(i + 1) };
}

export type ChipState = 'none' | 'current' | 'behind';

/** How an environment stands against the latest version: not deployed, on the latest, or on an older one. */
export function chipState(version: number | undefined, latest: number | null | undefined): ChipState {
  if (version === undefined) return 'none';
  return latest != null && version < latest ? 'behind' : 'current';
}

/** The environment a deploy of `version` opens on: the first (in the given order) not running it; else the first. */
export function defaultTarget<E extends { id: string }>(envs: E[], deployed: Record<string, number> | undefined, version: number): E | undefined {
  return envs.find((e) => deployed?.[e.id] !== version) ?? envs[0];
}

export type DeployKind = 'new' | 'update' | 'same' | 'rollback';

/** What deploying `target` to an environment running `current` (null = nothing) is. */
export function deployKind(current: number | null, target: number): DeployKind {
  if (current === null) return 'new';
  if (current === target) return 'same';
  return target < current ? 'rollback' : 'update';
}

/** The headline of a deploy item: "Currently runs v2" / "Not deployed". */
export function currentLabel(current: number | null): string {
  return current === null ? 'Not deployed' : `Currently runs v${current}`;
}

/** The latest published version of each selected model as deploy items; models without one are named in `skipped`. */
export function latestItems(models: Pick<VirtualModel, 'id' | 'name' | 'version'>[], selected: ReadonlySet<string>): { items: DeployItem[]; skipped: string[] } {
  const items: DeployItem[] = [];
  const skipped: string[] = [];
  for (const m of models) {
    if (!selected.has(m.id)) continue;
    if (m.version == null) skipped.push(m.name);
    else items.push({ modelId: m.id, version: m.version });
  }
  return { items, skipped };
}

// ---------------------------------------------------------------- the field table of a model (Settings → Your data models)

type Source = VirtualModel['fields'][number]['paths'][number];
type ModelLike = Pick<VirtualModel, 'fields'>;

/** The system of a source field: as set, else from its class name (the Cloud's lens does the same). */
export const sourceSystem = (p: Pick<Source, 'cls' | 'system'>): string => p.system ?? p.cls.slice(0, 3).toUpperCase();

/** The systems a model is made of, in the order its fields name them. */
export const modelSystems = (m: ModelLike): string[] => [...new Set(m.fields.flatMap((f) => f.paths.map(sourceSystem)))];

/** How many source fields a model has. */
export const sourceCount = (m: ModelLike): number => m.fields.reduce((n, f) => n + f.paths.length, 0);

/** What a model's chip says: nothing published, the latest version, or that the working copy differs from it. */
export function modelState(m: Pick<VirtualModel, 'version' | 'dirty'>): { text: string; tone: 'warn' | 'plain'; title: string } {
  if (m.version == null) return { text: 'Not published', tone: 'warn', title: 'Only a draft exists: publish a version to deploy it' };
  if (m.dirty) return { text: 'Draft changes', tone: 'warn', title: `The working copy differs from v${m.version}` };
  return { text: `v${m.version}`, tone: 'plain', title: `v${m.version} is the latest version` };
}

/** A field of the discovered model, as much as the table needs. */
export interface KnownField { path: string; count: number; effectiveType: string | null }

/** The discovered fields of the environment by class and path. */
export function knownFields(classes: readonly { name: string; fields: readonly KnownField[] }[] | undefined): Map<string, KnownField> {
  const out = new Map<string, KnownField>();
  for (const c of classes ?? []) for (const f of c.fields) out.set(`${c.name}|${f.path}`, f);
  return out;
}

/** The type of a model field from its source fields: one type, "mixed" when they differ, null when none is known yet. */
export function fieldType(paths: readonly Pick<Source, 'cls' | 'path'>[], known: ReadonlyMap<string, KnownField>): { label: string | null; mixed: boolean; types: string[] } {
  const types = [...new Set(paths.map((p) => known.get(`${p.cls}|${p.path}`)?.effectiveType).filter((t): t is string => !!t))];
  return { label: types.length === 0 ? null : types.length === 1 ? types[0]! : 'mixed', mixed: types.length > 1, types };
}

/** Which source fields of a model field the environment has seen. */
export function seenSources<S extends Pick<Source, 'cls' | 'path'>>(paths: readonly S[], known: ReadonlyMap<string, KnownField>): { seen: number; total: number; missing: S[] } {
  const missing = paths.filter((p) => !known.has(`${p.cls}|${p.path}`));
  return { seen: paths.length - missing.length, total: paths.length, missing };
}

/**
 * Which version the actual data shows: the one deployed to the environment, or the working copy (`draft`) when nothing is
 * deployed there (`not-deployed`) or when asked for (`asked`).
 */
export function lensMode(deployed: Record<string, number> | undefined, envId: string | undefined, askDraft: boolean): { draft: boolean; reason: 'not-deployed' | 'asked' | null; version: number | undefined } {
  const version = envId ? deployed?.[envId] : undefined;
  if (version === undefined) return { draft: true, reason: 'not-deployed', version };
  return askDraft ? { draft: true, reason: 'asked', version } : { draft: false, reason: null, version };
}

const quoted = (v: string) => (/[\s"()]/.test(v) || v === 'OR' ? `"${v.replace(/"/g, '')}"` : v);

/** The data search for the documents that have a model field: any of its source fields. */
export function fieldSearch(paths: readonly Pick<Source, 'cls' | 'path'>[]): string {
  const terms = paths.map((p) => `@class=${quoted(p.cls)} ${quoted(p.path)}=*`);
  return terms.length === 1 ? terms[0]! : terms.map((t) => `(${t})`).join(' OR ');
}

/** A lens cell as far as the table needs it. */
export interface Cell { state: 'agree' | 'conflict' | 'single'; systems: { system: string; value: string; cls: string; same: boolean }[] }

/** The data search for one entity: its key as each system writes it (2754101-6 OR FI27541016). */
export function entitySearch(key: string, keyCell: Cell | undefined): string {
  const values = [...new Set([key, ...(keyCell?.systems.map((s) => s.value) ?? [])].map((v) => v.trim()).filter(Boolean))];
  return values.map(quoted).join(' OR ');
}

/**
 * The actual values of a model field for one entity: one per system of the field, in the order of its sources. A system
 * that has the entity but no value for the field is listed empty; one that does not have the entity is left out.
 * `differs` marks a value other systems do not share (the field's systems disagree).
 */
export function actualValues(paths: readonly Pick<Source, 'cls' | 'system'>[], cell: Cell | undefined, entitySystems: readonly string[]): { system: string; cls: string | null; value: string | null; differs: boolean }[] {
  const systems = [...new Set(paths.map(sourceSystem))];
  const out: { system: string; cls: string | null; value: string | null; differs: boolean }[] = [];
  for (const system of systems) {
    const v = cell?.systems.find((s) => s.system === system);
    if (v) out.push({ system, cls: v.cls, value: v.value, differs: cell!.state === 'conflict' && !v.same });
    else if (entitySystems.includes(system)) out.push({ system, cls: null, value: null, differs: false });
  }
  // Values of a system the field does not name (an older version had it): shown too, after the others
  for (const v of cell?.systems ?? []) if (!systems.includes(v.system)) out.push({ system: v.system, cls: v.cls, value: v.value, differs: cell!.state === 'conflict' && !v.same });
  return out;
}
